import { defineStore } from 'pinia';

export interface State {
    navigationOpened: boolean;
    globalNavOpened: boolean;
    searchOpened: boolean;
    infoBannerOpened: boolean;
    adaptiveLanguageSwitcherOpened: boolean;
    metaNavCollapsed: boolean;
    mainNavCollapsed: boolean;
    subsiteNavExists: boolean;
    activatedTab: string;
}

interface TopMarginBaseValues {
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
}

export interface TopMarginOptions extends TopMarginBaseValues {
    prefix: 'mt' | 'top';
}

export interface TopMarginResult {
    class: string;
    style: Record<string, string>;
}

export const additionalTopMarginContentNewClasses = {
    mt: 'mt-[var(--content-offset-sm)] md:mt-[var(--content-offset-md)] lg:mt-[var(--content-offset-lg)] xl:mt-[var(--content-offset-xl)] transition-[margin-top]',
    top: 'top-[var(--content-offset-sm)] md:top-[var(--content-offset-md)] lg:top-[var(--content-offset-lg)] xl:top-[var(--content-offset-xl)] transition-[top]',
};

export const useNavigationStore = defineStore('navigationStore', {
    state: (): State => ({
        navigationOpened: false,
        globalNavOpened: false,
        searchOpened: false,
        infoBannerOpened: false,
        adaptiveLanguageSwitcherOpened: false,
        metaNavCollapsed: false,
        mainNavCollapsed: false,
        subsiteNavExists: false,
        activatedTab: '',
    }),
    getters: {
        bodyClasses(state): number {
            if (state.navigationOpened) {
                document.body.classList.add('overflow-hidden', 'md:overflow-auto');
                return 1;
            } else if (state.globalNavOpened || state.searchOpened) {
                document.body.classList.add('overflow-hidden');
                document.body.classList.remove('md:overflow-auto');
                return 2;
            } else {
                document.body.classList.remove('overflow-hidden', 'md:overflow-auto');
                return 3;
            }
        },
        additionalTopMarginContentOld(state): ({ prefix, sm, md, xl }: { prefix: string; sm: number; md: number; xl: number }) => string {
            // Note: if the values change, make sure all possible results are configured in the 'spacing' settings of the tailwind config
            return ({prefix, sm, md, xl}: { prefix: string; sm: number; md: number; xl: number }): string => {
                // add height of old nav bar
                sm += 14;
                md += 20;
                xl += 20;

                if (state.adaptiveLanguageSwitcherOpened) {
                    sm += 14;
                    md += 14;
                }
                if (state.infoBannerOpened) {
                    sm += 38;
                    md += 35;
                    xl += 35;
                }
                return `${prefix}-${sm} md:${prefix}-${md} xl:${prefix}-${xl}`;
            };
        },
        additionalTopMarginContentNew(state): (options?: TopMarginBaseValues) => Record<string, string> {
            return ({ sm = 0, md = 0, lg = 0, xl = 0 }: TopMarginBaseValues = {}): Record<string, string> => {
                // main nav mobile + tablet, always shown, always same size
                sm += 16;
                md += 16;

                // main nav desktop, only add if main nav is not collapsed
                if (!state.mainNavCollapsed) {
                    lg += 20;
                    xl += 20;

                    // meta nav desktop, only add if meta nav is not collapsed
                    if (!state.metaNavCollapsed) {
                        lg += 12;
                        xl += 12;
                    }
                }

                // sub site nav, only add if subsite nav is shown
                if (state.subsiteNavExists) {
                    sm += 13;
                    md += 13;
                    lg += 14;
                    xl += 14;
                }

                if (state.adaptiveLanguageSwitcherOpened) {
                    sm += 14;
                    md += 14;
                }
                if (state.infoBannerOpened) {
                    sm += 38;
                    md += 35;
                    lg += 35;
                    xl += 35;
                }

                return {
                    '--content-offset-sm': `${sm * 4}px`,
                    '--content-offset-md': `${md * 4}px`,
                    '--content-offset-lg': `${lg * 4}px`,
                    '--content-offset-xl': `${xl * 4}px`,
                };
            };
        },
        additionalTopMarginContent(): (options: TopMarginOptions) => TopMarginResult {
            return ({ prefix, sm = 0, md = 0, lg = 0, xl = 0 }: TopMarginOptions): TopMarginResult => {
                if (window['newNav']) {
                    return {
                        class: additionalTopMarginContentNewClasses[prefix],
                        style: this.additionalTopMarginContentNew({ sm, md, lg, xl }),
                    };
                }

                return {
                    class: this.additionalTopMarginContentOld({ prefix, sm, md, xl }),
                    style: {},
                };
            };
        },
        additionalTopMarginHeader(state): object {
            // Note: if the values change, make sure all possible results are configured in the 'spacing' settings of the tailwind config
            return ({prefix, sm, md, xl}: { prefix: string; sm: number; md: number; xl: number }): string => {
                if (state.infoBannerOpened) {
                    sm += 38;
                    md += 35;
                    xl += 35;
                }
                return `${prefix}-${sm} md:${prefix}-${md} xl:${prefix}-${xl}`;
            };
        }
    },
    actions: {
        setMetaNavCollapsed(collapsed: boolean): void {
            this.metaNavCollapsed = collapsed;
        },
        setMainNavCollapsed(collapsed: boolean): void {
            this.mainNavCollapsed = collapsed;
        },
        setSubsiteNavExists(exists: boolean): void {
            this.subsiteNavExists = exists;
        }
    }
});
